import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, IconButton, CircularProgress
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import { getSchoolAuths, saveSchoolAuth, deleteSchoolAuth } from '../../../store/actions/schoolAuthActions';
import { RootState } from '../../../store/reducers';

const SchoolAuthorizations = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const schoolAuthorizations = useSelector((state: RootState) => state.schoolAuths.schoolAuthorizations);
    const isFetching = useSelector((state: RootState) => state.schoolAuths.isFetching);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editItem, setEditItem] = useState<any>(null);
    const [formData, setFormData] = useState<any>({ Description: '', AuthNumber: '', AuthDate: '' });
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

    useEffect(() => {
        dispatch(getSchoolAuths());
    }, [dispatch]);

    const handleOpen = (item?: any) => {
        if (item) {
            setEditItem(item);
            setFormData({
                Description: item.Description || '',
                AuthNumber: item.AuthNumber || '',
                AuthDate: item.AuthDate ? item.AuthDate.substring(0, 10) : ''
            });
        } else {
            setEditItem(null);
            setFormData({ Description: '', AuthNumber: '', AuthDate: '' });
        }
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setEditItem(null);
        setFormData({ Description: '', AuthNumber: '', AuthDate: '' });
    };

    const handleSave = () => {
        const payload = editItem ? { ...formData, id: editItem.id } : { ...formData };
        dispatch(saveSchoolAuth(payload));
        handleClose();
        enqueueSnackbar('Salvo com sucesso!', { variant: 'success' });
    };

    const handleDelete = (id: number) => {
        dispatch(deleteSchoolAuth(id));
        setDeleteConfirm(null);
        enqueueSnackbar('Excluido com sucesso!', { variant: 'success' });
    };

    const handleChange = (field: string, value: string) => {
        setFormData((prev: any) => ({ ...prev, [field]: value }));
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('pt-BR');
        } catch {
            return dateStr;
        }
    };

    const columns = [
        {
            name: 'id',
            label: 'ID',
            options: {
                display: false,
                filter: false,
                sort: true,
            }
        },
        {
            name: 'Description',
            label: 'Descrição',
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: 'AuthNumber',
            label: 'Número da Autorização',
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: 'AuthDate',
            label: 'Data da Autorização',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value: string) => formatDate(value),
            }
        },
        {
            name: 'actions',
            label: 'Ações',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex: number) => {
                    const item = schoolAuthorizations[dataIndex];
                    return (
                        <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(item.id);
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    );
                }
            }
        }
    ];

    const options = {
        filterType: 'checkbox' as const,
        selectableRows: 'none' as const,
        responsive: 'standard' as const,
        rowsPerPage: 15,
        rowsPerPageOptions: [15, 30, 50],
        textLabels: {
            body: { noMatch: 'Nenhum registro encontrado', toolTip: 'Ordenar' },
            pagination: { next: 'Próxima', previous: 'Anterior', rowsPerPage: 'Linhas por página:', displayRows: 'de' },
            toolbar: { search: 'Buscar', downloadCsv: 'Download CSV', print: 'Imprimir', viewColumns: 'Colunas', filterTable: 'Filtrar' },
            filter: { all: 'Todos', title: 'Filtros', reset: 'Limpar' },
        },
        onRowClick: (_rowData: any, rowMeta: any) => {
            const item = schoolAuthorizations[rowMeta.dataIndex];
            handleOpen(item);
        },
    };

    if (isFetching) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Autorizações Escolares</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
                    Nova Autorização
                </Button>
            </Box>

            <Paper>
                <MUIDataTable
                    title=""
                    data={schoolAuthorizations || []}
                    columns={columns}
                    options={options}
                />
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editItem ? 'Editar Autorização' : 'Nova Autorização'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Descrição"
                            value={formData.Description}
                            onChange={(e) => handleChange('Description', e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Número da Autorização"
                            value={formData.AuthNumber}
                            onChange={(e) => handleChange('AuthNumber', e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Data da Autorização"
                            type="date"
                            value={formData.AuthDate}
                            onChange={(e) => handleChange('AuthDate', e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" disabled={!formData.Description}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja excluir esta autorização?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                    <Button
                        onClick={() => deleteConfirm !== null && handleDelete(deleteConfirm)}
                        color="error"
                        variant="contained"
                    >
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SchoolAuthorizations;
